import apiClient from '@/lib/apiClient';
import { ApiResponse, PaginationParams, User } from '@/types';

export interface UsersResponse {
  rows: User[];
  total: number;
  totalPages: number;
  currentPage: number;
}

export const userService = {
  getAll: async (params?: PaginationParams): Promise<UsersResponse> => {
    const { data } = await apiClient.get<ApiResponse<User[]>>('/users', { params });
    return {
      rows: data.data ?? [],
      total: (data.meta?.total as number) ?? 0,
      totalPages: (data.meta?.totalPages as number) ?? 1,
      currentPage: (data.meta?.currentPage as number) ?? 1,
    };
  },

  getById: async (id: number): Promise<User> => {
    const { data } = await apiClient.get<ApiResponse<User>>(`/users/${id}`);
    if (!data.data) throw new Error('User not found');
    return data.data;
  },

  getDepartments: async (): Promise<string[]> => {
    const { data } = await apiClient.get<ApiResponse<string[]>>('/users/departments');
    return data.data ?? [];
  },

  update: async (
    id: number,
    payload: Partial<
      Pick<
        User,
        | 'name'
        | 'email'
        | 'dateOfBirth'
        | 'dateOfJoining'
        | 'designationLevel'
        | 'designationDept'
        | 'address'
        | 'pincode'
        | 'city'
        | 'state'
        | 'employeeCode'
        | 'isActive'
        | 'monthlyTarget'
        | 'yearlyTarget'
      >
    >,
  ): Promise<User> => {
    const { data } = await apiClient.patch<ApiResponse<User>>(`/users/${id}`, payload);
    if (!data.data) throw new Error('Failed to update user');
    return data.data;
  },

  completeOnboarding: async (
    // One shape for every role. `designationDept` is absent deliberately: the
    // server derives the department from the job role requested at
    // registration, and its schema is `.strict()`, so sending one is a 422
    // rather than a silently-ignored field.
    payload: Pick<
      User,
      | 'dateOfBirth'
      | 'dateOfJoining'
      | 'designationLevel'
      | 'address'
      | 'pincode'
      | 'city'
      | 'state'
      | 'employeeCode'
    >,
  ): Promise<User> => {
    const { data } = await apiClient.post<ApiResponse<User>>('/users/me/onboarding', payload);
    if (!data.data) throw new Error('Onboarding failed');
    return data.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },

  // ─── Registration approvals (admin) ──────────────────────────────────────────
  getPending: async (): Promise<User[]> => {
    const { data } = await apiClient.get<ApiResponse<User[]>>('/users/pending');
    return data.data ?? [];
  },

  approve: async (id: number): Promise<User> => {
    const { data } = await apiClient.patch<ApiResponse<User>>(`/users/${id}/approve`);
    if (!data.data) throw new Error('Failed to approve user');
    return data.data;
  },

  reject: async (id: number): Promise<User> => {
    const { data } = await apiClient.patch<ApiResponse<User>>(`/users/${id}/reject`);
    if (!data.data) throw new Error('Failed to reject user');
    return data.data;
  },
};
